/**
 * Type Definitions: Widget Configuration
 * SpecReferenceID: ADMIN-V1
 * Requirements: ADMIN-V1-R16, CONFIG-V1-R18, WIDGET-V1-R28
 *
 * TypeScript interfaces for the WIDGET-V1 runtime
 * configuration. Mirrors the Pydantic schema in
 * `src/backend/src/app/schemas/widget_config.py` so the
 * admin editor and the backend admin write path stay in
 * lockstep.
 *
 * Two type families:
 *   - `WidgetConfig*` : working types used by the admin
 *     React form. Field names mirror the snake_case schema
 *     directly (no camelCase transform) because the widget
 *     config is not consumed by hot UI rendering paths
 *     where camelCase matters; keeping the wire shape
 *     avoids a bidirectional mapper for a small blob.
 *   - `WidgetConfigApiResponse` : raw API payload type, the
 *     same shape as `WidgetConfig` plus the version /
 *     audit-metadata wrapper fields.
 */

/**
 * The five WIDGET-V1 surfaces the widget can render. Pinned
 * verbatim from the backend `WidgetSurface` Literal in
 * `app/schemas/widget_config.py`. Adding a surface here
 * without also adding it to the backend (or vice versa)
 * causes the admin form to silently desync from validation.
 */
export const WIDGET_SURFACES = [
  'chat',
  'galen-search',
  'semantic-search',
  'simple-search',
  'browse',
] as const;

export type WidgetSurface = (typeof WIDGET_SURFACES)[number];

/**
 * Active widget configuration blob.
 *
 * CONFIG-V1-R18 storage shape; the admin form edits this
 * directly and posts it back to `PUT /v1/config/widget`.
 */
export interface WidgetConfig {
  /**
   * Subset of the five WIDGET-V1 surfaces this deployment
   * exposes. Empty array = nothing rendered (safe-default).
   */
  enabled_surfaces: WidgetSurface[];

  /**
   * CSS custom property overrides applied to the widget's
   * root element. Keys are CSS variable names (e.g.
   * 'primary', 'accent', 'font-family'); values are
   * CSS-valid strings. Empty object = built-in neutral
   * theme.
   */
  theme: Record<string, string>;

  /**
   * Host domains permitted to embed this deployment's
   * widget. Displayed in the admin UI for reference; actual
   * enforcement happens via CORS at the API gateway.
   */
  allowed_origins: string[];

  /**
   * Optional deployment-specific knobs (default landing
   * surface, welcome message, safety validator UI toggles).
   * Free-form escape hatch — stable fields may be promoted
   * to named columns in future iterations.
   */
  deployment_settings: Record<string, unknown>;

  /**
   * AB#4401: display name the Chat surface shows for the
   * assistant ("Galen" by default; per-deployment so a
   * customer can white-label). Optional here so the admin
   * form tolerates a config blob from a backend that
   * pre-dates the field — the form falls back to "Galen".
   */
  assistant_name?: string;

  /**
   * AB#4404: optional HTML block the widget renders
   * (sanitised) at the top of the mounted surface — a
   * per-deployment intro / landing blurb. Empty / absent =
   * no blurb. Optional here so the admin form tolerates a
   * config blob from a backend that pre-dates the field.
   */
  landing_content?: string;

  /**
   * WIDGET-V1-R28: number of guideline section-hierarchy
   * levels the Browse widget table of contents lists (1-5,
   * default 2). Optional here so the admin form tolerates a
   * config blob from a backend that pre-dates the field —
   * the form falls back to 2.
   */
  browse_toc_depth?: number;
}

/**
 * WIDGET-V1-R28: bounds and default for `browse_toc_depth`,
 * pinned to the backend `WidgetConfig` Field(ge=1, le=5,
 * default=2).
 */
export const BROWSE_TOC_DEPTH_MIN = 1;
export const BROWSE_TOC_DEPTH_MAX = 5;
export const DEFAULT_BROWSE_TOC_DEPTH = 2;

/**
 * Response payload from `GET /v1/config/widget` and
 * `PUT /v1/config/widget`. Wraps `WidgetConfig` with the
 * version + audit metadata the storage layer tracks.
 */
export interface WidgetConfigApiResponse {
  config: WidgetConfig;
  version: number;
  updated_at: string;
  updated_by: string;
}

/**
 * Request payload for `PUT /v1/config/widget`.
 * Single-field wrapper so the admin form can post the
 * whole blob in one go (no partial-merge semantics).
 */
export interface WidgetConfigUpdateRequest {
  config: WidgetConfig;
}

/**
 * Safe-default WidgetConfig used as the initial form state
 * when the admin page mounts before the API call resolves.
 * Matches the backend `WidgetConfig()` defaults and the
 * Alembic migration seed row exactly.
 */
export const DEFAULT_WIDGET_CONFIG: WidgetConfig = {
  enabled_surfaces: [],
  theme: {},
  allowed_origins: [],
  deployment_settings: {},
  assistant_name: 'Galen',
  landing_content: '',
  browse_toc_depth: DEFAULT_BROWSE_TOC_DEPTH,
};
